// Angular
import { Injectable } from '@angular/core';

// Third party
import * as _ from 'lodash';
import moment from 'moment';

// Local library
import {
  arrangewagermatrix_removedup_dashes,
  IGetEventsDetailsResponse,
  IPool,
  IPoolTotals,
  IToteMatrixSlimModel,
  toDictionary,
  TRACK_COLOR,
  TRACK_TYPE_lIST,
} from '@amwager/util-ts';
import { ISelectedRace } from '../+state/wager.models';

/**
 * WIN / PLC / SHW pool totals dictionary
 */
export interface IWPSPoolsTotals {
  WIN: IPoolTotals & { TakeoutRates: number };
  PLC: IPoolTotals & { TakeoutRates: number };
  SHW: IPoolTotals & { TakeoutRates: number };
}

/**
 * Return type of MatrixService.buildPoolsTotals
 */
export interface IPoolsTotalsBuildResult {
  singleLegDict: IWPSPoolsTotals;
  multiLegDict: Record<string, IPoolTotals & { TakeoutRates: number }>;
  multiRaceDict: Record<string, IPoolTotals & { TakeoutRates: number }>;
}

/**
 * Pure data-transformation helpers for the wager Pool Data view
 */
@Injectable()
export class MatrixService {

  /**
   * Last-resort chart colours. TRACK_COLOR only carries palettes for
   * Thoroughbred/Quarterhorse/Harness/Greyhound and stops at saddle cloth 30,
   * so anything outside that would otherwise plot with no colour at all.
   */
  private readonly FALLBACK_CHART_COLORS = [
    '#1C498E', '#C0392B', '#1E8449', '#8E44AD', '#D35400',
    '#16A085', '#7D6608', '#2C3E50', '#A93226', '#117864'
  ];

  /**
   * Build the unique list of pool ids (always prepends `TRU`)
   * @param {ISelectedRace} selectedRace Selected race
   * @returns {string[]} Pool ids list
   */
  buildPoolList(selectedRace?: ISelectedRace): string[] {
    if (!selectedRace?.PoolList) return ['TRU'];

    return [
      'TRU',
      ...(selectedRace?.PoolList?.split(',') || []),
    ];
  }

  /**
   * Decorate EX/QU/DBL matrices with fairValueClass and favouriteBetClass
   * @param {any} matrix Dictionary of pool matrices keyed by pool id
   * @param {'EX' | 'QU' | 'DBL'} type Matrix pool to decorate
   * @returns {any} New decorated matrix object
   */
  updateMatrix(
    matrix: any,
    type: 'EX' | 'QU' | 'DBL'
  ): any {
    if (!matrix) return matrix;

    const TO_UPDATE_MATRIX: any = _.get(matrix, type);

    if (_.isEmpty(TO_UPDATE_MATRIX)) return matrix;

    let minPrice = 1000000;

    _.map(TO_UPDATE_MATRIX?.Rows, (rowEx) => {
      _.map(rowEx?.Columns, (colEx: any) => {
        if (colEx?.Price && !colEx?.Invalid && minPrice > colEx?.Price) {
          minPrice = colEx.Price;
        }
      });
    });

    const MATRIX_ROWS = _.map(TO_UPDATE_MATRIX?.Rows, (rowEx, indexRow) => {
      const COLUMNS = _.map(rowEx?.Columns, (colEx: any, indexCol) => {
        let firstOdd = 0;
        let secondOdd = 0;
        let currentPrice = 0;
        let fairValue: number | null = null;
        let fairValueClass = '';

        if (colEx?.Invalid) return colEx;

        // Only the EX pool exposes the fair-value heat map. QU / DBL
        // just show the favourite-bet highlight.
        if (type === 'EX') {
          firstOdd =
            (Number(matrix['WIN']?.Rows?.[0]?.Columns?.[indexRow]?.Price) || 0)
            / ((matrix['WIN']?.PriceBaseWager / 2) || 0);
          secondOdd =
            (Number(matrix['WIN']?.Rows?.[0]?.Columns?.[indexCol]?.Price) || 0)
            / ((matrix['WIN']?.PriceBaseWager / 2) || 0);

          fairValue = firstOdd * (secondOdd + 1);
          currentPrice = colEx.Price / (TO_UPDATE_MATRIX.PriceBaseWager / 2);

          if (fairValue > 0) {
            if (fairValue <= currentPrice * 0.75)
              fairValueClass = 'fairValueLesser';
            else if (fairValue >= currentPrice * 1.25)
              fairValueClass = 'fairValueGreater';
          }
        }

        return {
          ...colEx,
          data:
            colEx?.Price / (TO_UPDATE_MATRIX?.PriceBaseWager / 2) || '-',
          fairValueClass: fairValueClass || '',
          favouriteBetClass: minPrice === colEx.Price ? 'favouriteBet' : '',
        };
      });

      return {
        ...rowEx,
        Columns: COLUMNS,
      };
    });

    return {
      ...matrix,
      [type]: {
        ...TO_UPDATE_MATRIX,
        Rows: MATRIX_ROWS,
      },
    };
  }

  /**
   * Group PoolTotals into single-leg WPS, multi-position, and multi-race buckets
   * @param {any} racePoolsTotals PoolTotals payload
   * @param {ISelectedRace} selectedRace Selected race
   * @param {IGetEventsDetailsResponse} selectedEventTrack Selected event track (TakeoutRates)
   * @returns {IPoolsTotalsBuildResult} Three-bucket pools totals
   */
  buildPoolsTotals(
    racePoolsTotals: any,
    selectedRace?: ISelectedRace,
    selectedEventTrack?: IGetEventsDetailsResponse
  ): IPoolsTotalsBuildResult {

    let singleLeg: any[] = [];
    let multiLeg: any[] = [];
    let multiRace: any[] = [];

    _.forEach(selectedRace?.Pools, (pool: IPool) => {
      if (pool?.PoolRaces === 1) {
        if (pool?.Legs === 1) singleLeg.push({ poolId: pool?.PoolId, id: pool?.PoolId });
        else multiLeg.push({ poolId: pool?.PoolId, id: pool?.PoolId });
      } else {
        multiRace.push({ poolId: pool?.PoolId, id: pool?.PoolId });
      }
    });

    const applyTakeoutRates = (entry: any) => ({
      ...entry,
      TakeoutRates: (selectedEventTrack?.TakeoutRates as any)?.[entry?.poolId],
    });

    _.forEach(racePoolsTotals?.PoolTotals, (pool: IPoolTotals) => {

      singleLeg = _.map(singleLeg, (leg) => {
        if (leg?.poolId === pool?.PoolId) {
          return applyTakeoutRates({ ...leg, ...pool });
        }
        return applyTakeoutRates(leg);
      });

      multiLeg = _.map(multiLeg, (leg) => {
        if (leg?.poolId === pool?.PoolId) {
          return applyTakeoutRates({ ...leg, ...pool });
        }
        return applyTakeoutRates(leg);
      });

      multiRace = _.map(multiRace, (race) => {
        if (race?.poolId === pool?.PoolId) {
          return applyTakeoutRates({ ...race, ...pool });
        }
        return applyTakeoutRates(race);
      });
    });

    return {
      singleLegDict: toDictionary(singleLeg) as unknown as IWPSPoolsTotals,
      multiLegDict: toDictionary(multiLeg) as any,
      multiRaceDict: toDictionary(multiRace) as any,
    };
  }

  /**
   * Build Win Table rows (last 5 unique snapshots with increase/decrease remarks)
   * @param {IToteMatrixSlimModel[]} winRaceAllOddsMatrixes WIN matrix snapshots
   * @returns {any[]} Win table rows (newest first)
   */
  buildWinTable(winRaceAllOddsMatrixes: IToteMatrixSlimModel[]): any[] {
    if (_.isEmpty(winRaceAllOddsMatrixes)) return [];

    let winTablePreviousData: any;
    const previousMatrixDataOddList: any[] = [];

    _.forEach(winRaceAllOddsMatrixes, (odds) => {
      const localTime = new Date(odds.TimeStamp + 'Z');
      const total: any[] = [];

      let hours: string | number = localTime.getHours();
      let meridiem = '';
      let minutes: string | number = localTime.getMinutes();
      let seconds: string | number = localTime.getSeconds();
      let currentTime = '';

      if (hours >= 12) {
        meridiem = 'PM';
        hours = hours - 12;
      } else {
        meridiem = 'AM';
      }

      hours = hours < 10 ? '0' + hours : hours;
      minutes = minutes < 10 ? '0' + minutes : minutes;
      seconds = seconds < 10 ? '0' + seconds : seconds;
      currentTime = hours + ':' + minutes + ':' + seconds + ' ' + meridiem;

      if (!winTablePreviousData) winTablePreviousData = odds;

      let alreadyExist = false;
      _.map(previousMatrixDataOddList, (data) => {
        if (data?.time === currentTime) alreadyExist = true;
      });

      if (alreadyExist) return;

      _.forEach(odds?.Row?.[0]?.Columns, (col: any, colIndex) => {
        let previousData: any = winTablePreviousData?.Row?.[0]?.Columns;
        let remark: string;

        previousData = previousData?.[colIndex];

        if (
          _.isEmpty(previousData)
          || _.isEmpty(col)
          || !previousData?.DecimalOdds
          || !col?.DecimalOdds
          || col.DecimalOdds === previousData.DecimalOdds
        ) {
          remark = '';
        } else {
          remark = (col.DecimalOdds || 0) < previousData.DecimalOdds
            ? 'increase'
            : 'decrease';
        }

        total.push({
          Runner: col.ColumnNumber,
          odds: col.DecimalOdds || '-',
          odd_remark: remark,
          cur: col.DecimalOdds,
          prev: previousData.DecimalOdds || '',
        });
      });

      if (previousMatrixDataOddList?.length >= 5) previousMatrixDataOddList.pop();

      previousMatrixDataOddList.unshift({
        time: currentTime,
        totals: total,
      });
      winTablePreviousData = odds;
    });

    return previousMatrixDataOddList;
  }

  /**
   * Convert matrix snapshots into `[time, decimalOdds]` point series (last 20 min to post)
   * @param {IToteMatrixSlimModel[]} matrix Matrix snapshots
   * @param {string} postTime Race post time
   * @returns {any[]} Chart point series
   */
  buildChartPoints(
    matrix: IToteMatrixSlimModel[] | undefined,
    postTime?: string
  ): any[] {
    const tempDataWINChart: any[] = [];

    if (_.isEmpty(matrix)) return tempDataWINChart;

    const normalizedPostTime = postTime?.replace('Z', '');

    _.forEach(matrix, (odds) => {
      const time = moment(normalizedPostTime).diff(
        moment(odds?.TimeStamp?.replace('Z', '')),
        'minutes',
        true
      );

      _.forEach(odds?.Row?.[0]?.Columns, (col: any, colIndex) => {
        if (time > 20) return;
        if (_.isEmpty(tempDataWINChart[colIndex])) {
          tempDataWINChart.push({
            points: { show: false },
            lines: { show: true, width: 2 },
            color: col.ColumnNumber || '',
            label: '#' + col.ColumnNumber,
            data: [[time, col.DecimalOdds]],
          });
        } else {
          tempDataWINChart[colIndex]?.data.push([time, col.DecimalOdds]);
        }
      });
    });

    return tempDataWINChart;
  }

  /**
   * Drop dashes, dedupe, and trim to the latest N series
   * @param {any[]} rawSeries Raw series
   * @param {number} limit Max series to keep
   * @returns {any[]} Trimmed series
   */
  trimChartSeries(rawSeries: any[], limit: number = 20): any[] {
    // OLD: returned the shared helper's output as-is. That helper seeds its
    // accumulator with a placeholder `{}`, which survives when no series
    // qualifies — the chart then read one point-less, colourless series as
    // "has data" and rendered empty axes instead of the no-odds message.
    // return arrangewagermatrix_removedup_dashes(rawSeries).splice(-limit);
    const plotable = _.filter(
      arrangewagermatrix_removedup_dashes(rawSeries),
      (series: any) => !_.isEmpty(series?.data)
    );

    return plotable.splice(-limit);
  }

  /**
   * Resolve the silk colour for a runner (whites/yellows mapped to legible greys)
   * @param {number} trackType Track type number
   * @param {number} index Runner index
   * @returns {string} Colour hex or name
   */
  getColorRunner(trackType: number | undefined, index: number): string {
    // OLD: unknown track types (JaiAlai, Arabian, Mixed, ...) and saddle
    // cloths past the palette returned undefined, which CanvasJS draws as an
    // invisible line/marker — the "colourless chart" symptom.
    // let color = TRACK_COLOR[TRACK_TYPE_lIST[trackType || 1]]?.[index];
    const palette =
      TRACK_COLOR[TRACK_TYPE_lIST[trackType || 1]] || TRACK_COLOR['Thoroughbred'];

    let color = palette?.[index];

    if (!color) {
      const slot = Math.abs(Number(index) || 0) % this.FALLBACK_CHART_COLORS.length;
      color = this.FALLBACK_CHART_COLORS[slot];
    }

    if (color === 'white') color = '#e2e2e2';
    if (color === 'yellow') color = '#c5c50f';

    return color;
  }

  /**
   * Build per-runner chart series for a single pool (WIN or TRU)
   * @param {IToteMatrixSlimModel[]} winRaceAllOddsMatrixes Matrix snapshots
   * @param {ISelectedRace} selectedRace Selected race (Runners/Scratch/PostTime)
   * @returns {any[]} Series data ready for buildChartOptions
   */
  buildWinChartArrayData(
    winRaceAllOddsMatrixes: IToteMatrixSlimModel[] | undefined,
    selectedRace?: ISelectedRace
  ): any[] {
    if (_.isEmpty(winRaceAllOddsMatrixes)) return [];

    const tempData = this.buildChartPoints(winRaceAllOddsMatrixes, selectedRace?.PostTime);
    const trimmed = this.trimChartSeries(tempData);

    const scratched: any[] = [];
    _.forEach(selectedRace?.Runners, (runner: any) => {
      if (runner?.Scratch) scratched.push(runner?.RunnerId);
    });

    return _.filter(
      _.map(trimmed, (winData: any) => {
        if (scratched.includes(winData?.color)) return undefined;
        return {
          ...winData,
          data: _.map(winData?.data, (dataPoint: any) => ({
            [`odds${winData?.color}`]: dataPoint[1] || 0,
            [`mtp${winData?.color}`]: dataPoint[0] || 0,
          })) || [],
        };
      }),
      (entry) => entry
    );
  }

  /**
   * Combined WIN + TRU series for the Win Chart "BOTH" filter
   * @param {IToteMatrixSlimModel[]} winMatrixes WIN matrix snapshots
   * @param {IToteMatrixSlimModel[]} truMatrixes TRU matrix snapshots
   * @param {ISelectedRace} selectedRace Selected race (Runners/PostTime)
   * @returns {any[]} Combined series data
   */
  buildBothChartArrayData(
    winMatrixes: IToteMatrixSlimModel[] | undefined,
    truMatrixes: IToteMatrixSlimModel[] | undefined,
    selectedRace?: ISelectedRace
  ): any[] {
    const winPoints = this.trimChartSeries(
      this.buildChartPoints(winMatrixes, selectedRace?.PostTime)
    );
    const truPoints = this.trimChartSeries(
      this.buildChartPoints(truMatrixes, selectedRace?.PostTime)
    );

    const scratched: any[] = [];
    _.forEach(selectedRace?.Runners, (runner: any) => {
      if (runner?.Scratch) scratched.push(runner?.RunnerId);
    });

    const shape = (series: any[], pool: 'WIN' | 'TRU') =>
      _.filter(
        _.map(series, (winData: any) => {
          if (scratched.includes(winData?.color)) return undefined;
          return {
            ...winData,
            data: _.map(winData?.data, (dataPoint: any) => ({
              [`odds${winData?.color}${pool}`]: dataPoint[1] || 0,
              [`mtp${winData?.color}${pool}`]: dataPoint[0] || 0,
            })) || [],
            xKey: `mtp${winData?.color}${pool}`,
            yKey: `odds${winData?.color}${pool}`,
            pool,
          };
        }),
        (entry) => entry
      );

    return [...shape(winPoints, 'WIN'), ...shape(truPoints, 'TRU')];
  }

  /**
   * Build the ag-charts options blob for the Win Chart
   * @param {any[]} arrayData Series array from buildWinChartArrayData/buildBothChartArrayData
   * @param {number} trackType Track type number (runner colour lookup)
   * @param {'WIN' | 'TRU' | 'BOTH'} filter Active chart filter
   * @returns {any} ag-charts options blob
   */
  buildChartOptions(
    arrayData: any[],
    trackType: number | undefined,
    filter: 'WIN' | 'TRU' | 'BOTH'
  ): any {
    let seriesData: any[] = [];

    if (filter === 'BOTH') {
      seriesData = _.map(arrayData, (data: any) => ({
        type: 'line',
        xKey: data?.xKey,
        yKey: data?.yKey,
        yName: `#${data?.color}`,
        lineDash: data?.pool === 'WIN' ? [0, 0] : [4, 2],
        marker: {
          fill: this.getColorRunner(trackType, data?.color),
          size: 5,
          stroke: this.getColorRunner(trackType, data?.color),
          strokeWidth: 1,
          shape: data?.pool === 'WIN' ? 'diamond' : 'circle',
        },
        stroke: this.getColorRunner(trackType, data?.color),
        strokeWidth: 1,
      }));
    } else {
      seriesData = _.map(arrayData, (data: any) => ({
        type: 'line',
        xKey: `mtp${data?.color}`,
        yKey: `odds${data?.color}`,
        yName: `#${data?.color}`,
        marker: {
          fill: this.getColorRunner(trackType, data?.color),
          size: 5,
          stroke: this.getColorRunner(trackType, data?.color),
          strokeWidth: 1,
          shape: filter === 'WIN' ? 'diamond' : 'circle',
        },
        stroke: this.getColorRunner(trackType, data?.color),
        strokeWidth: 1,
      }));
    }

    return {
      data: _.flatten(_.map(arrayData, (data: any) => data?.data || [])),
      theme: {
        overrides: {
          line: {
            series: {
              highlightStyle: {
                series: {
                  dimOpacity: 0.2,
                  strokeWidth: 2,
                  size: 8,
                },
              },
            },
          },
        },
      },
      series: seriesData,
      axes: [
        {
          type: 'number',
          position: 'bottom',
          label: { avoidCollisions: true },
          reverse: true,
          min: -2,
          nice: true,
          tick: { interval: 2 },
          title: { text: 'Minutes To Post' },
          gridLine: { style: [{ stroke: 'white' }] },
        },
        {
          type: 'number',
          position: 'left',
          label: { minSpacing: 100, avoidCollisions: true },
          min: 1,
          nice: true,
          tick: { interval: 10 },
          title: {
            text:
              (filter === 'WIN'
                ? 'WIN'
                : filter === 'TRU'
                  ? 'TRU'
                  : filter === 'BOTH'
                    ? 'Both'
                    : '') + ' Odds',
          },
          gridLine: { style: [{ stroke: '#e2e2e2' }] },
        },
      ],
      legend: { item: { paddingY: 15 } },
      autoSize: true,
    };
  }

  /**
   * Build a CanvasJS options blob for the Win Chart
   * @param {any[]} arrayData Series array from buildWinChartArrayData/buildBothChartArrayData
   * @param {number} trackType Track type number (runner colour lookup)
   * @param {'WIN' | 'TRU' | 'BOTH'} filter Active chart filter
   * @returns {any} CanvasJS options object
   */
  buildCanvasJsOptions(
    arrayData: any[],
    trackType: number | undefined,
    filter: 'WIN' | 'TRU' | 'BOTH'
  ): any {
    const data = _.map(arrayData, (series: any) => {
      const runnerId = series?.color;
      const pool: 'WIN' | 'TRU' | undefined = series?.pool;

      const dataPoints = _.map(series?.data || [], (point: any) => {
        const mtpKey = _.find(_.keys(point), (k) => k.startsWith('mtp'));
        const oddsKey = _.find(_.keys(point), (k) => k.startsWith('odds'));
        return {
          x: (mtpKey ? point[mtpKey] : 0) || 0,
          y: (oddsKey ? point[oddsKey] : 0) || 0,
        };
      });

      const runnerColor = this.getColorRunner(trackType, runnerId);

      const markerType =
        filter === 'BOTH'
          ? (pool === 'WIN' ? 'square' : 'circle')
          : (filter === 'WIN' ? 'square' : 'circle');

      const lineDashType =
        filter === 'BOTH' && pool === 'TRU' ? 'dash' : 'solid';

      return {
        type: 'line',
        name: `#${runnerId}`,
        showInLegend: true,
        color: runnerColor,
        lineColor: runnerColor,
        lineThickness: 2,
        lineDashType,
        markerType,
        markerSize: 6,
        markerColor: runnerColor,
        markerBorderColor: runnerColor,
        markerBorderThickness: 1,
        dataPoints,
      };
    });

    return {
      animationEnabled: true,
      zoomEnabled: false,
      backgroundColor: 'transparent',
      axisX: {
        title: 'Minutes To Post',
        reversed: true,
        minimum: -2,
        interval: 2,
        gridColor: 'transparent',
      },
      axisY: {
        title:
          (filter === 'WIN'
            ? 'WIN'
            : filter === 'TRU'
              ? 'TRU'
              : 'Both') + ' Odds',
        minimum: 1,
        interval: 10,
        gridColor: '#e2e2e2',
      },
      toolTip: {
        shared: false,
      },
      legend: {
        cursor: 'pointer',
        verticalAlign: 'bottom',
        horizontalAlign: 'center',
        itemclick: (e: any) => {
          e.dataSeries.visible =
            e.dataSeries.visible === undefined || e.dataSeries.visible
              ? false
              : true;
          e.chart.render();
        },
      },
      data,
    };
  }

}
